/**
 * BilansPage - List of accounting statements
 *
 * Displays all bilans with filters and creation modal.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useBilanStore } from '../store/bilanStore';
import { useAccountStore } from '../store/accountStore';
import { BilanCard, BilanFormModal } from '../components/bilans';
import { CreateAccountingStatementRequest, UpdateAccountingStatementRequest } from '@budgira/shared';

const BilansPage = () => {
  const { t } = useTranslation();
  const {
    statements,
    totalPages,
    isLoading,
    error,
    limit,
    isSubmitting,
    filters,
    page,
    fetchStatements,
    createStatement,
    deleteStatement,
    checkLimit,
    setFilters,
    setPage,
    clearError,
  } = useBilanStore();

  const { accounts, fetchAccounts } = useAccountStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [compareId, setCompareId] = useState<string | null>(null);

  // Fetch data on mount
  useEffect(() => {
    fetchStatements();
    fetchAccounts();
    checkLimit();
  }, [fetchStatements, fetchAccounts, checkLimit]);

  // Refetch when filters or page change
  useEffect(() => {
    fetchStatements();
  }, [filters, page, fetchStatements]);

  const handleCreate = async (data: CreateAccountingStatementRequest | UpdateAccountingStatementRequest) => {
    const result = await createStatement(data as CreateAccountingStatementRequest);
    if (result) {
      setIsFormOpen(false);
    }
  };

  const handleDelete = async (id: string) => {
    const success = await deleteStatement(id);
    if (success) {
      setDeleteConfirmId(null);
    }
  };

  const handleCompare = (id: string) => {
    setCompareId(id);
  };

  const canCreate = limit?.canCreate ?? true;
  const activeAccounts = accounts.filter((a) => !a.isArchived);

  // Generate year options (last 10 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('bilans.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('bilans.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {limit && !canCreate && (
            <span className="text-sm text-amber-600 dark:text-amber-400">
              {t('bilans.limitReached', { count: limit.count, limit: limit.limit })}
            </span>
          )}
          <button
            onClick={() => setIsFormOpen(true)}
            disabled={!canCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-4 h-4" />
            {t('bilans.create')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder={t('bilans.searchPlaceholder')}
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div className="w-32">
          <select
            value={filters.year || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                year: e.target.value ? parseInt(e.target.value) : undefined,
              })
            }
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="">{t('bilans.allYears')}</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="w-48">
          <select
            value={filters.accountId || ''}
            onChange={(e) =>
              setFilters({ ...filters, accountId: e.target.value || undefined })
            }
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="">{t('bilans.allAccounts')}</option>
            {activeAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && statements.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <DocumentIcon className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('bilans.empty')}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            {t('bilans.emptyDescription')}
          </p>
          {canCreate && (
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('bilans.createFirst')}
            </button>
          )}
        </div>
      )}

      {/* Statements grid */}
      {!isLoading && statements.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {statements.map((statement) => (
            <BilanCard
              key={statement.id}
              statement={statement}
              onDelete={(id) => setDeleteConfirmId(id)}
              onCompare={handleCompare}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      <BilanFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        accounts={activeAccounts}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirmId(null)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('bilans.deleteConfirmTitle')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('bilans.deleteConfirmMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare redirect */}
      {compareId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setCompareId(null)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('bilans.selectToCompare')}
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {statements
                .filter((s) => s.id !== compareId)
                .map((s) => (
                  <Link
                    key={s.id}
                    to={`/bilans/compare?id1=${compareId}&id2=${s.id}`}
                    className="block p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                  >
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {s.name}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {new Date(s.startDate).toLocaleDateString()} -{' '}
                      {new Date(s.endDate).toLocaleDateString()}
                    </p>
                  </Link>
                ))}
            </div>
            <button
              onClick={() => setCompareId(null)}
              className="mt-4 w-full px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export default BilansPage;
